/**
 * The browser's own speech recogniser, wrapped into something a roster can use.
 *
 * `SpeechRecognition` is a live API in Chrome and Safari but has never been standardised,
 * so it ships under a vendor prefix, has no TypeScript definition for the recogniser itself
 * (only for its result objects), and stops on its own whenever it feels like it. All three
 * are handled here so the panel above can think in terms of "listening" and "heard this".
 *
 * WHERE THE AUDIO GOES. Neither implementation runs on the device: Chrome streams the
 * microphone to Google and Safari to Apple. For a proof of concept that is a decision worth
 * stating rather than burying — children's names leave the building. A production version
 * would want either an on-device model or a school-controlled transcription endpoint, and
 * that choice is independent of everything else in this folder: only this file would change.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The recogniser, as much of it as this file touches.
 *
 * `SpeechRecognitionResultList` and friends *are* in lib.dom; the recogniser and its two
 * events are not, so they are declared here rather than reached for with `any`.
 */
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechResultEvent) => void) | null;
  onerror: ((event: SpeechErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

interface SpeechResultEvent {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechErrorEvent {
  error: string;
  message?: string;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

interface SpeechCapableWindow {
  SpeechRecognition?: SpeechRecognitionCtor;
  webkitSpeechRecognition?: SpeechRecognitionCtor;
}

function recognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as SpeechCapableWindow;
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Whether this browser can do it at all. Safe to call during render. */
export function isSpeechRecognitionSupported(): boolean {
  return recognitionCtor() !== null;
}

export interface Utterance {
  transcript: string;
  /** The recogniser's own 0–1 confidence, where it reports one. */
  confidence: number;
}

export type SpeechError =
  /** The teacher (or the OS) refused microphone access. Nothing to retry. */
  | 'permission'
  /** Recognition needs a connection and did not have one. */
  | 'network'
  /** This browser has no recogniser. */
  | 'unsupported'
  /** Anything else the recogniser reported. */
  | 'failed';

interface Options {
  /** BCP-47 tag handed to the recogniser. */
  lang?: string;
  /** Called once per finished utterance — a pause in speech, not a press of a button. */
  onUtterance: (utterance: Utterance) => void;
}

/**
 * Restarts that count as a storm rather than the ordinary end of an utterance.
 *
 * Chrome ends a recognition session on every silence and after roughly a minute regardless,
 * so a session that is meant to stay open has to restart itself constantly. That makes a
 * genuinely broken recogniser — one erroring the instant it opens — indistinguishable from
 * normal operation except by rate, which is what this counts.
 */
const RESTART_STORM_COUNT = 5;
const RESTART_STORM_WINDOW_MS = 4_000;

export interface SpeechRecognitionState {
  supported: boolean;
  listening: boolean;
  /** What is being said right now, before the recogniser commits to it. */
  interim: string;
  error: SpeechError | null;
  start: () => void;
  stop: () => void;
}

export function useSpeechRecognition({ lang = 'en-US', onUtterance }: Options): SpeechRecognitionState {
  const [supported] = useState(isSpeechRecognitionSupported);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState<SpeechError | null>(null);

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  // Whether the teacher still wants to be listened to, as opposed to whether the recogniser
  // happens to be running — the two diverge constantly, which is the whole problem here.
  const wantedRef = useRef(false);
  const restartsRef = useRef<number[]>([]);

  // Held in a ref so a re-render of the panel never tears down a live session.
  const onUtteranceRef = useRef(onUtterance);
  useEffect(() => { onUtteranceRef.current = onUtterance; }, [onUtterance]);

  const stop = useCallback(() => {
    wantedRef.current = false;
    restartsRef.current = [];
    setListening(false);
    setInterim('');
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    if (!recognition) return;
    recognition.onresult = null;
    recognition.onerror = null;
    recognition.onend = null;
    recognition.onstart = null;
    try { recognition.abort(); } catch { /* already gone */ }
  }, []);

  const start = useCallback(() => {
    const Ctor = recognitionCtor();
    if (!Ctor) {
      setError('unsupported');
      return;
    }
    if (recognitionRef.current) return;

    setError(null);
    wantedRef.current = true;
    restartsRef.current = [];

    const open = () => {
      if (!wantedRef.current) return;

      const recognition = new Ctor();
      recognition.lang = lang;
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        setListening(true);
      };

      recognition.onresult = event => {
        let pending = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          const alternative = result[0];
          if (!alternative) continue;
          if (result.isFinal) {
            const transcript = alternative.transcript.trim();
            if (transcript) {
              onUtteranceRef.current({ transcript, confidence: alternative.confidence ?? 0 });
            }
          } else {
            pending += alternative.transcript;
          }
        }
        setInterim(pending.trim());
      };

      recognition.onerror = event => {
        // A silence is not a failure, and an abort is this hook's own doing.
        if (event.error === 'no-speech' || event.error === 'aborted') return;
        if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
          setError('permission');
          wantedRef.current = false;
          return;
        }
        setError(event.error === 'network' ? 'network' : 'failed');
      };

      recognition.onend = () => {
        setInterim('');
        if (!wantedRef.current) {
          setListening(false);
          return;
        }
        // Ordinary: the recogniser closed on a pause and the session should carry on.
        const now = Date.now();
        restartsRef.current = [...restartsRef.current, now].filter(
          t => now - t < RESTART_STORM_WINDOW_MS,
        );
        if (restartsRef.current.length >= RESTART_STORM_COUNT) {
          wantedRef.current = false;
          setListening(false);
          setError('failed');
          return;
        }
        recognitionRef.current = null;
        open();
      };

      recognitionRef.current = recognition;
      try {
        recognition.start();
      } catch {
        // `start()` on a recogniser that is already running throws; the session it is
        // complaining about is the one we want, so there is nothing to do about it.
      }
    };

    open();
  }, [lang]);

  // A teacher who navigates away mid-dismissal should not leave a microphone open.
  useEffect(() => stop, [stop]);

  return { supported, listening, interim, error, start, stop };
}
